import React from "react";
import styled from "styled-components";
import {Link, graphql, useStaticQuery} from "gatsby";
import Image from 'gatsby-image';

import ProjectSection from "./projectSection";

import Colors from "../../constants/colors";
import Fonts from "../../constants/fonts";
import Breakpoints from "../../constants/breakpoints";

import IconHome from "../../images/icons/icon-home.svg";
import IconHomeHover from "../../images/icons/icon-home-hover.svg";
import IconPrevious from "../../images/icons/icon-arrow_previous.svg";
import IconNext from "../../images/icons/icon-arrow_next.svg";

const getProjects = graphql`
  query {
    projects:allContentfulProject {
      edges {
        node {
          title
          order
          description {
            description
          }
          slug
          shortDescription
          projectFooterImage {
            fluid {
              ...GatsbyContentfulFluid_withWebp
            }
          }
        }
      }
    }
  }
`;


const LinkContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;

  @media only screen and (max-width: ${Breakpoints.Width.tablet_small}) {
    flex-direction: column;
  }
`;

const LinkBox = styled(Link)`
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 600px;
  height: 200px;
  border-radius: 8px;
  background-image: linear-gradient(90deg, transparent 8px, #FFFFFF 8px);
  box-shadow: 0 2px 6px rgba(0,0,0, 0.1);
  text-decoration: none;
  color: ${Colors.Text.default};
  overflow: hidden;
  transition: all 0.2s ease-in-out;

  @media only screen and (max-width: ${Breakpoints.Width.project_medium}) {
    flex-direction: column;
    height: auto;
    background-image: linear-gradient(transparent 8px, #FFFFFF 8px);
  }

  @media only screen and (max-width: ${Breakpoints.Width.tablet_small}) {
    flex-direction: row;
    height: 200px;
    background-image: linear-gradient(90deg, transparent 8px, #FFFFFF 8px);
  }

  @media only screen and (max-width: ${Breakpoints.Width.mobile_large}) {
    flex-direction: column;
    height: auto;
    background-image: linear-gradient(transparent 8px, #FFFFFF 8px);
  }

  &:hover, &:active, &:focus {
    box-shadow: 0px 2px 2px rgba(54,54,54,0.5), 0px 4px 4px rgba(54,54,54,0.5), 0px 8px 8px rgba(54,54,54,0.5), 0px 16px 16px rgba(54,54,54,0.5),  0px 16px 16px rgba(54,54,54,0.5);
    transform: translateY(-4px);
    
    h4 {
      color: ${Colors.Text.link};
    }

    .project__thumbnail {
      transform: scale(1.1);
    }
  }

  &:not(:last-child) {
    margin-right: 24px;
    margin-bottom: 0;

    @media only screen and (max-width: ${Breakpoints.Width.tablet_small}) {
      margin-right: 0;
      margin-bottom: 20px;
    }
  }
`;

const Thumbnail = styled(Image)`
  transform: scale(1.01);
  transition: all 0.5s ease-in-out;
  width: 100%;
  max-width: 240px;
  border-right: 1px solid ${Colors.Separator.project_footer};
  
  @media only screen and (max-width: ${Breakpoints.Width.laptop_medium}) {
    max-width: 210px;
    
  }

  @media only screen and (max-width: ${Breakpoints.Width.laptop_small}) {
    max-width: 180px;
  }

  @media only screen and (max-width: ${Breakpoints.Width.project_medium}) {
    max-width: 100%;
    height: 200px;
    border-right: none;
    border-bottom: 1px solid ${Colors.Separator.project_footer};
  }

  @media only screen and (max-width: ${Breakpoints.Width.tablet_small}) {
    max-width: 200px;
    min-width: 200px;
    height: auto;
  }

  @media only screen and (max-width: ${Breakpoints.Width.mobile_large}) {
    max-width: 100%;
    height: 200px;
    border-right: none;
    border-bottom: 1px solid ${Colors.Separator.project_footer};
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;

  @media only screen and (max-width: ${Breakpoints.Width.project_medium}) {
   padding-top: 32px;
  }

  @media only screen and (max-width: ${Breakpoints.Width.tablet_small}) {
    padding-top: 20px;
  }

  @media only screen and (max-width: ${Breakpoints.Width.mobile_large}) {
   padding-top: 32px;
  }
  
  .project-footer__indicator {
    font-family: ${Fonts.Font_family.sans_serif_condensed};
    font-size: 17px;
    line-height: 24px;
    padding-left: 32px;
    margin-bottom: 28px;

    background-repeat: no-repeat;
    background-position: 0 0;
  }

  h4 {
    font-family: ${Fonts.Font_family.title_condensed};
    font-size: 30px;
    line-height: 30px;
    font-weight: bold;
    margin-bottom: 4px;
    color: ${Colors.Text.emphasized};
  }

  p {
    font-size: 14px;
    line-height: 24px;
    margin: 0;
  }
`;

const LinkPrevious = styled(LinkBox)`
  .project-footer__indicator {
    color: ${Colors.Underline.teal};
    background-image: url(${IconPrevious});
  }
`;

const LinkNext = styled(LinkBox)`
  .project-footer__indicator {
    color: ${Colors.Underline.pink};
    background-image: url(${IconNext});
  }
`;

const LinkHome = styled(Link)`
  font-family: ${Fonts.Font_family.title_condensed};
  font-size: 16px;
  font-weight: bold;
  line-height: 20px;
  letter-spacing: 0.8px;
  text-decoration: none;
  color: ${Colors.Basic.gray_88};
  padding: 25px 24px 23px 68px;
  border-radius: 35px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  background-color: transparent;
  background-image: url(${IconHome});
  background-repeat: no-repeat;
  background-position: 20px 50%;
  transition: all 0.2s ease-in-out;
  align-self: center;
  margin-top: 80px;

  &:hover, &:active, &:focus {
    color: ${Colors.Basic.orange_01};
    border: 1px solid ${Colors.Basic.orange_01};
    background-color: rgba(0, 0, 0, 0.05);
    background-image: url(${IconHomeHover});
    box-shadow: 0px 2px 2px rgba(54,54,54,0.5), 0px 4px 4px rgba(54,54,54,0.5), 0px 8px 8px rgba(54,54,54,0.5), 0px 16px 16px rgba(54,54,54,0.5);
    /* transform: translateY(-4px); */
  }
`;

const ProjectFooter = props => {
  const currentProjectOrder = parseInt(props.currentProjectOrder);
  const data = useStaticQuery(getProjects);
  const projects = data.projects.edges;
  const previousProject = projects.find(
    (project) => {
      return (project.node.order === currentProjectOrder - 1);
    }
  );
  const nextProject = projects.find(
    (project) => {
      if (projects.length === currentProjectOrder) {
        return (project.node.order === 1);
      } else {
        return (project.node.order === currentProjectOrder + 1);
      }
    }
  );
  
  return (
    <ProjectSection paddingTop="160px" paddingBottom="60px" bgColor={Colors.Basic.gray_blue_01} >
      <LinkContainer data-sal="slide-up" data-sal-delay="500" data-sal-duration="800">
        {previousProject &&
          <LinkPrevious to={"/portfolio/" + previousProject.node.slug} className={previousProject.node.slug}>
            <Thumbnail className="project-footer__thumbnail" fluid={previousProject.node.projectFooterImage.fluid}/>
            <Info className="project-footer__info">
              <div className="project-footer__indicator">Previous Project</div>
              <h4>{previousProject.node.title}</h4>
              <p>{previousProject.node.shortDescription}</p>
            </Info>
          </LinkPrevious>
        }
        <LinkNext to={"/portfolio/" + nextProject.node.slug} className={nextProject.node.slug}>
          <Thumbnail className="project-footer__thumbnail" fluid={nextProject.node.projectFooterImage.fluid}/>
          <Info className="project-footer__info">
            <div className="project-footer__indicator">Next Project</div>
            <h4>{nextProject.node.title}</h4>
            <p>{nextProject.node.shortDescription}</p>
          </Info>
        </LinkNext>
      </LinkContainer>
      <LinkContainer data-sal="fade" data-sal-delay="800" data-sal-duration="800">
        <LinkHome to="/#portfolio">
          Back to Homepage
        </LinkHome>
      </LinkContainer>
    </ProjectSection>
  );
};


export default ProjectFooter;